import { AppLink } from '@/components/links/AppLink';
import { useManifest } from '@/contexts/ManifestContext';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';

interface HeaderCTAButtonProps {
  className?: string;
  onClick?: () => void;
  /** `sm` (desktop bar, ~32px/13px) or `lg` (mobile bar & slide menu, 50px/16px). */
  size?: 'sm' | 'lg';
}

/**
 * Manifest-driven call-to-action shown in the top navigation (and at the bottom
 * of the mobile slide menu). Renders nothing unless `header.cta.display` is true.
 * Colors come from the theme tokens `--color-cta` / `--color-cta-text`, so each
 * white-label can configure its own CTA color. The target `url` may be an
 * internal route or an absolute URL — `AppLink` handles both.
 */
const HeaderCTAButton = ({
  className,
  onClick,
  size = 'sm',
}: HeaderCTAButtonProps) => {
  const { header, locale, features } = useManifest();
  const { i18n } = useTranslation();
  const cta = header.cta;

  if (!cta?.display) return null;

  const label =
    cta.label[i18n.language] ??
    cta.label[locale.default] ??
    Object.values(cta.label)[0];

  if (!label) return null;

  const sizeClasses =
    size === 'lg' ? 'h-[50px] px-6 text-base' : 'h-8 px-4 text-[13px]';

  return (
    <AppLink
      href={cta.url}
      onClick={onClick}
      className={cn(
        'bg-cta text-cta-text rounded-button font-display inline-flex items-center justify-center text-center font-bold whitespace-nowrap transition-opacity hover:opacity-90',
        sizeClasses,
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {label}
    </AppLink>
  );
};

export default HeaderCTAButton;
